import React, { useEffect, useRef, useState } from "react";
import ReactCrop, { centerCrop } from "react-image-crop";
import { GIFEncoder, quantize, applyPalette } from "gifenc";
import { canvasToFile } from "utils/constant";
import "react-image-crop/dist/ReactCrop.css";

/**
 * Loads the source and, if it is a GIF (checked by magic bytes, so it works
 * for blob:/data: URLs with no extension), returns { blob, buffer }.
 * Returns null for non-GIFs or if the source can't be read (e.g. CORS).
 */
const loadGif = async (src) => {
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    const header = await blob.slice(0, 4).text(); // "GIF8" for GIF87a / GIF89a
    if (header !== "GIF8") return null;
    const buffer = await blob.arrayBuffer();
    return { blob, buffer };
  } catch (e) {
    return null;
  }
};

const nextTick = () => new Promise((r) => setTimeout(r, 0));

/**
 * Crops every frame of a GIF and re-encodes it with gifenc, keeping the animation.
 * Frames are decoded by the browser's ImageDecoder (already fully composited).
 * `rect` is in ORIGINAL image pixels: { x, y, width, height }.
 * `isCancelled` lets us abort if the user keeps dragging the crop box.
 * Throws if ImageDecoder is not supported (caller falls back to server crop).
 */
const cropGif = async (buffer, rect, isCancelled) => {
  if (typeof window === "undefined" || typeof window.ImageDecoder === "undefined") {
    throw new Error("ImageDecoder not supported");
  }

  const { x, y, width, height } = rect;
  const decoder = new window.ImageDecoder({ data: buffer, type: "image/gif" });

  try {
    await decoder.tracks.ready;
    await decoder.completed;
    const frameCount = decoder.tracks.selectedTrack.frameCount;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const encoder = GIFEncoder();

    for (let i = 0; i < frameCount; i++) {
      if (isCancelled()) return null;

      const { image: frame } = await decoder.decode({ frameIndex: i });

      // Draw only the crop region of this frame onto the output canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(frame, x, y, width, height, 0, 0, width, height);
      const delay = frame.duration ? Math.round(frame.duration / 1000) : 100; // µs -> ms
      frame.close();

      const { data } = ctx.getImageData(0, 0, width, height);
      const palette = quantize(data, 256, {
        format: "rgba4444",
        oneBitAlpha: true,
      });
      const index = applyPalette(data, palette, "rgba4444");
      const transparentIndex = palette.findIndex((c) => c[3] === 0);
      const hasTransparency = transparentIndex >= 0;

      encoder.writeFrame(index, width, height, {
        palette,
        delay: delay || 100,
        transparent: hasTransparency,
        transparentIndex: hasTransparency ? transparentIndex : 0,
        dispose: hasTransparency ? 2 : -1,
        repeat: 0, // loop forever
      });

      if (i % 3 === 0) await nextTick(); // keep the UI responsive
    }

    encoder.finish();
    return new Blob([encoder.bytes()], { type: "image/gif" });
  } finally {
    decoder.close();
  }
};

export default function CropProfile({
  setParentCrop = () => {},
  setCropPreview = () => {},
  // Fallback: crop rectangle in ORIGINAL image pixels, only set if the
  // in-browser GIF crop fails or is unsupported (send it to the backend with
  // the original file). Null otherwise.
  setParentCropData = () => {},
  locked = false,
  ...props
}) {
  const { image, type, returnType } = props;
  const imgSrc = image;
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [gif, setGif] = useState(null); // { blob, buffer } | null
  const [processing, setProcessing] = useState(false);
  const zoom = 1;
  const [aspect, setAspect] = useState(undefined);

  // Detect GIFs whenever the source changes
  useEffect(() => {
    let cancelled = false;
    setGif(null);
    if (imgSrc) {
      loadGif(imgSrc).then((result) => {
        if (!cancelled) setGif(result);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [imgSrc]);

  // Build the output whenever the crop (or detected GIF) changes
  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    const run = async () => {
      if (!completedCrop?.width || !completedCrop?.height || !imgRef.current) return;
      const el = imgRef.current;

      // Static frame: used for the preview and for non-GIF output
      const { blobImage, image: imageFile, base64Canvas } = await canvasToFile(
        el,
        completedCrop,
        zoom,
        0,
        "crop-image-free-mode",
        type
      );
      if (cancelled) return;

      if (!gif) {
        if (blobImage) {
          setParentCropData(null);
          setParentCrop(returnType === "blob" ? blobImage : imageFile);
          setCropPreview(base64Canvas);
        }
        return;
      }

      // GIF: crop in original-image pixels
      const scaleX = el.naturalWidth / el.width;
      const scaleY = el.naturalHeight / el.height;
      const x = Math.max(0, Math.round(completedCrop.x * scaleX));
      const y = Math.max(0, Math.round(completedCrop.y * scaleY));
      const width = Math.max(
        1,
        Math.min(el.naturalWidth - x, Math.round(completedCrop.width * scaleX))
      );
      const height = Math.max(
        1,
        Math.min(el.naturalHeight - y, Math.round(completedCrop.height * scaleY))
      );
      const rect = { x, y, width, height };

      if (base64Canvas) setCropPreview(base64Canvas);

      setProcessing(true);
      try {
        // Small debounce so we don't re-encode on every tiny adjustment
        await new Promise((r) => setTimeout(r, 250));
        if (cancelled) return;

        const croppedBlob = await cropGif(gif.buffer, rect, isCancelled);
        if (cancelled || !croppedBlob) return;

        const croppedFile = new File([croppedBlob], "profile.gif", {
          type: "image/gif",
        });
        setParentCropData(null); // already cropped, nothing for the server to do
        setParentCrop(returnType === "blob" ? croppedBlob : croppedFile);
      } catch (e) {
        // Fallback: original GIF + coordinates for server-side cropping
        if (cancelled) return;
        const originalFile = new File([gif.blob], "profile.gif", {
          type: "image/gif",
        });
        setParentCrop(returnType === "blob" ? gif.blob : originalFile);
        setParentCropData(rect);
      } finally {
        if (!cancelled) setProcessing(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCrop, gif]);

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    let cropSize = Math.min(width, height);
    let defaultM = {
      width: 180,
      height: 180,
    };
    let finalW = defaultM.width;
    let finalH = defaultM.height;

    //calculate aspect ratio
    let aw = finalH / finalW;
    let aspectRatioW = aw * defaultM.width;

    let ah = finalW / finalH;
    let aspectRatioH = ah * defaultM.height;

    //set aspect ratio
    let aspectRatio = aspectRatioH / aspectRatioW;
    setAspect(aspectRatio);

    setCrop(
      centerCrop(
        {
          unit: "px",
          width: cropSize - 10,
          height: cropSize - 10,
        },
        width,
        height
      )
    );
  }

  return (
    <div className="min-w-[375px]">
      <div
        className="w-full relative bg-passwordGray rounded-md flex justify-center items-center"
        style={{ minHeight: "400px" }}
      >
        <div
          className="w-full overflow-y-auto custom-scroll flex justify-center items-center"
          style={{ maxHeight: "73vh" }}
        >
          {!!imgSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              locked={locked}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                onLoad={onImageLoad}
                style={{ maxHeight: "400px", maxWidth: "400px", objectFit: "cover" }}
              />
            </ReactCrop>
          )}
        </div>
      </div>
      {processing && (
        <p className="text-xs text-lightBlack mt-2 text-center">
          Processing GIF…
        </p>
      )}
    </div>
  );
}